import { useMutation } from "@tanstack/react-query";
import { actions } from "astro:actions";
import { navigate } from "astro:transitions/client";
import Link from "astro-typesafe-routes/link/react";
import { $path } from "astro-typesafe-routes/path";
import { toast } from "sonner";

import { Alert } from "~/components/Alert";
import { AssignmentForm } from "~/components/AssignmentForm";
import { DialogBody } from "~/components/DialogBody";
import { DialogFooter } from "~/components/DialogFooter";
import { DialogHeader } from "~/components/DialogHeader";
import { QuotaAlert } from "~/components/QuotaAlert";
import { useQuota } from "~/hooks/quota";
import { trackEvent } from "~/utils/analytics";
import type { FragmentType } from "~/utils/fragment";
import { slugify } from "~/utils/slug";

import type { AssignPack_PackFragment } from "./AssignPack_PackFragment";

export const AssignPack: React.FC<{
  pack: FragmentType<"Pack", { select: typeof AssignPack_PackFragment }>;
}> = ({ pack }) => {
  const quota = useQuota();

  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: actions.assignment.create.orThrow,
    onSuccess: (assignment) => {
      trackEvent("assignment_created");
      navigate(
        $path({
          to: "/account/assignments/[slug]",
          params: {
            slug: slugify(assignment),
          },
        }),
      );
    },
    onError: (error) => {
      toast.error("Failed to create assignment", {
        description: error.message,
      });
    },
  });

  return (
    <form
      className="contents"
      onSubmit={(event) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        mutate(formData);
      }}
    >
      <DialogHeader>Assign pack</DialogHeader>
      <DialogBody>
        <input type="hidden" name="packId" value={pack.id} />
        <AssignmentForm pack={pack}>
          <QuotaAlert type="assignment" quota={quota.assignments}>
            <Alert
              type="info"
              description={
                <>
                  Read our introductory{" "}
                  <Link
                    className="link"
                    to="/support/guides/[...slug]"
                    params={{
                      "...slug": "introducing-assignments",
                    }}
                  >
                    guide to assignments
                  </Link>
                  .
                </>
              }
            >
              Need help getting started?
            </Alert>
          </QuotaAlert>
        </AssignmentForm>
      </DialogBody>
      <DialogFooter>
        <button
          disabled={!quota.assignments || isPending || isSuccess}
          type="submit"
          className="btn btn-secondary btn-block"
        >
          Create assignment
        </button>
      </DialogFooter>
    </form>
  );
};
